import {fetch} from "utils/dataAccess";

/**
 * Plain-JSON calls to the personal shopper endpoints of the Marketplace API, on top of the
 * shared data-access helper (token, organizationId, 401 handling, abort on navigation). The
 * helper speaks hydra and hands a refusal back as an Error carrying the message only; the
 * conversation screen needs the status too (404 not mirrored yet, 409 someone else holds the
 * conversation, 422 the WhatsApp window is closed), so the API relays it in the body as
 * "status" and this wrapper rebuilds a PersonalShopperError from it.
 */
export class PersonalShopperError extends Error {
    constructor(message, status, details = {}) {
        super(message);
        this.name = "PersonalShopperError";
        this.status = status;
        this.details = details;
    }
}

function withQuery(path, query) {
    const parameters = new URLSearchParams();
    Object.entries(query).forEach(([name, value]) => {
        if (value !== undefined && value !== null && value !== "" && value !== false) {
            parameters.set(name, String(value));
        }
    });
    const encoded = parameters.toString();

    return encoded === "" ? path : `${path}${path.includes("?") ? "&" : "?"}${encoded}`;
}

function statusOf(json) {
    if (Number.isInteger(json.status)) {
        return json.status;
    }
    if (Number.isInteger(json.code)) {
        return json.code;
    }
    if (json["hydra:description"] === "Not Found" || json.detail === "Not Found") {
        return 404;
    }

    return 500;
}

export default async function request(path, {method = "GET", body = undefined, query = {}, signal = undefined} = {}) {
    const headers = new Headers({Accept: "application/json"});
    if (body !== undefined) {
        headers.set("Content-Type", "application/json");
    }

    let refusal = null;
    const options = {method, headers, body: body !== undefined ? JSON.stringify(body) : undefined};
    if (signal !== undefined) {
        // null keeps the helper from attaching the global abort signal: a poll must outlive a
        // navigation-triggered abort of the page's other GETs.
        options.signal = signal;
    }

    let response;
    try {
        response = await fetch(withQuery(path, query), options, false, true, (json) => {
            refusal = json;
        });
    } catch (error) {
        if (refusal === null) {
            throw error;
        }
        const {error: message, status, ...details} = refusal;

        throw new PersonalShopperError(message || refusal["hydra:description"] || refusal.detail || error.message, statusOf(refusal), details);
    }

    if (!response || response.status === 204) {
        return null;
    }

    return response.json();
}
